import { Component, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ToastService } from '../../services/toast.service';
import { SettingsService, ProjectSettings } from '../../services/settings.service';
import { I18nService } from '../../services/i18n.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, RouterModule],
  template: `
    <div class="min-h-screen flex items-center justify-center px-4 relative overflow-hidden">
      <div class="bg-white/90 backdrop-blur-md rounded-2xl shadow-2xl p-8 w-full max-w-md z-10 border border-white/20">
        <div class="flex flex-col items-center mb-8">
          <img *ngIf="settings?.logoUrl" [src]="settings?.logoUrl" class="h-16 w-auto mb-4 object-contain" alt="Logo">
          <h1 class="text-3xl font-bold text-gray-900 text-center">{{ i18n.t('dashboard.title') }}</h1>
          <h2 class="text-lg font-medium text-gray-500 mt-2">{{ i18n.t('login.subtitle') }}</h2>
        </div>

        <form (ngSubmit)="onLogin()" class="space-y-4">
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 mb-1">{{ i18n.t('login.email') }}</label>
            <input
              type="email"
              id="email"
              [(ngModel)]="email"
              name="email"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition"
              [placeholder]="i18n.t('login.email')"
              required
            />
          </div>

          <div>
            <label for="password" class="block text-sm font-medium text-gray-700 mb-1">{{ i18n.t('login.password') }}</label>
            <input
              type="password"
              id="password"
              [(ngModel)]="password"
              name="password"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition"
              [placeholder]="i18n.t('login.password')"
              required
            />
          </div>

          <button
            type="submit"
            [disabled]="loading"
            class="btn-system-primary w-full py-3"
          >
            @if (loading) {
              {{ i18n.t('login.loading') }}
            } @else {
              {{ i18n.t('login.button') }}
            }
          </button>
        </form>

        <div class="mt-4 text-center">
          <a [routerLink]="['/forgot-password']" class="text-sm text-blue-500 hover:text-blue-700 font-medium">
            Forgot Password?
          </a>
        </div>

        <p class="mt-6 text-center text-gray-600">
          Don't have an account?
          <a [routerLink]="['/register']" class="text-blue-500 hover:text-blue-700 font-semibold cursor-pointer">Register</a>
        </p>
      </div>
    </div>
  `,
})
export class LoginComponent {
  email = '';
  password = '';
  loading = false;
  error = '';
  settings: ProjectSettings | null = null;

  constructor(
    private authService: AuthService,
    private router: Router,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private settingsService: SettingsService,
    public i18n: I18nService
  ) {
    this.settingsService.settings$.subscribe(s => {
      this.settings = s;
      this.cdr.detectChanges();
    });
  }

  onLogin(): void {
    if (!this.email || !this.password) {
      this.toastService.error(this.i18n.t('login.email') + ' & ' + this.i18n.t('login.password') + ' required');
      return;
    }

    this.loading = true;
    this.cdr.detectChanges();

    this.authService.login(this.email, this.password).subscribe({
      next: () => {
        // Login set cookies — now fetch user profile to hydrate auth state before navigating
        this.authService.getCurrentUser().subscribe({
          next: () => {
            this.ngZone.run(() => {
              this.toastService.success(this.i18n.t('toast.loginSuccess'));
              this.router.navigate(['/dashboard']);
              this.loading = false;
              this.cdr.detectChanges();
            });
          },
          error: () => {
            this.ngZone.run(() => {
              this.toastService.error(this.i18n.t('toast.loginError'));
              this.loading = false;
              this.cdr.detectChanges();
            });
          }
        });
      },
      error: (err) => {
        this.ngZone.run(() => {
          const errorMessage = err.error?.error || this.i18n.t('toast.loginError');
          this.toastService.error(errorMessage);
          this.loading = false;
          this.cdr.detectChanges();
        });
      },
    });
  }
}
